import { CartItem } from './types'
import { formatCurrency } from './utils'
import { aggregateQuantityOptions } from './utils'

export interface WhatsAppOrderData {
  restaurantName: string
  whatsappNumber: string
  currencySymbol: string
  orderNumber: string
  customerName: string
  customerPhone: string
  deliveryType?: string
  paymentMethod?: string
  items: CartItem[]
  subtotal: number
  deliveryFee: number
  total: number
  deliveryAddress?: string | null
  mapsUrl?: string | null
  notes?: string | null
  ticketImageUrl?: string | null
  trackingToken: string
  customerTrackingVisible?: boolean
  deliveryDate?: string | null
  deliveryTimeSlot?: string | null
  paymentProofUrl?: string | null
  // Restaurant location (for pickup / dine_in messages so the customer's
  // saved home address is not leaked into the kitchen WhatsApp).
  restaurantAddress?: string | null
  restaurantMapsUrl?: string | null
  // MS Express external delivery — when usesExternalDelivery is true and the
  // order is a delivery, the totals block shows the food subtotal and the
  // MS Express delivery breakdown (zone / locality / cost) separately.
  usesExternalDelivery?: boolean
  deliveryZone?: string | null
  deliverySubzone?: string | null
  foodSubtotal?: number | null
}

function formatDeliveryType(type?: string): string {
  switch (type) {
    case 'delivery':
      return 'Delivery'
    case 'pickup':
      return 'Para llevar'
    case 'dine_in':
      return 'Comer aquí'
    default:
      return type || 'N/A'
  }
}

function formatPaymentMethod(method?: string): string {
  switch (method) {
    case 'sinpe':
      return 'SINPE Móvil'
    case 'cash':
      return 'Efectivo'
    case 'card':
      return 'Tarjeta'
    default:
      return method || 'N/A'
  }
}

export function buildWhatsAppMessage(data: WhatsAppOrderData): string {
  const cs = data.currencySymbol
  const fmt = (n: number) => n.toLocaleString()
  const trackingUrl = `${window.location.origin}/track/${data.trackingToken}`

  // Build numbered items list
  const itemsList = data.items
    .map((item, idx) => {
      const optTotal = item.selected_options.reduce(
        (sum, opt) => sum + opt.price_delta,
        0,
      )
      const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity

      let line = `${idx + 1}. ${item.item_name_snapshot} (x${item.quantity}) — ${cs}${fmt(lineTotal)}`

      for (const opt of aggregateQuantityOptions(item.selected_options)) {
        const trimmedName = (opt.option_name || '').trim()
        // Heurística: si el nombre de la opción ya es un monto en moneda
        // (ej. "₡2,000", "$5", "2500") no agregamos el `+₡delta` para
        // evitar confusión al cliente, ya que el delta ya está
        // implícito en el line total.
        const nameLooksLikeMoney =
          /[₡$€£¥]\s?\d/.test(trimmedName) || /^\d[\d.,]*$/.test(trimmedName)
        const delta =
          opt.price_delta > 0 && !nameLooksLikeMoney
            ? ` +${cs}${fmt(opt.price_delta)}`
            : ''
        const qtySuffix = opt.qty > 1 ? ` x${opt.qty}` : ''
        line += `\n   ${opt.option_name}${qtySuffix}${delta}`
      }

      if (item.notes) {
        line += `\n   📝 ${item.notes}`
      }

      return line
    })
    .join('\n\n')

  // Build message
  let msg = ''

  msg += `🔥 *NUEVO PEDIDO — ${data.restaurantName}*\n\n`

  msg += `📦 *Pedido #${data.orderNumber}*\n`
  msg += `👤 Cliente: ${data.customerName}\n`
  msg += `📞 Tel: ${data.customerPhone}\n\n`

  // Preorder delivery info
  if (data.deliveryDate || data.deliveryTimeSlot) {
    msg += `📅 *ENTREGA PROGRAMADA:*\n`
    if (data.deliveryDate) {
      msg += `   Fecha: ${data.deliveryDate}\n`
    }
    if (data.deliveryTimeSlot) {
      msg += `   Horario: ${data.deliveryTimeSlot}\n`
    }
    msg += `\n`
  }

  msg += `🛒 *PEDIDO:*\n\n`
  msg += `${itemsList}\n\n`

  // MS Express external delivery — show food subtotal separate from the
  // MS Express delivery breakdown. Only when the flag is on AND it's a
  // delivery; otherwise fall back to the standard totals block unchanged.
  const showMsExpressBlock =
    data.usesExternalDelivery === true && data.deliveryType === 'delivery'

  if (showMsExpressBlock) {
    const food = data.foodSubtotal ?? data.subtotal
    msg += `💰 SUBTOTAL COMIDA: ${cs}${fmt(food)}\n`
    msg += `\n🚚 *DELIVERY MS EXPRESS*\n`
    if (data.deliveryZone) {
      msg += `   Zona: ${data.deliveryZone}\n`
    }
    if (data.deliverySubzone) {
      msg += `   Localidad: ${data.deliverySubzone}\n`
    }
    msg += `   Costo delivery: ${cs}${fmt(data.deliveryFee)}\n`
    msg += `\n✅ *TOTAL A PAGAR: ${cs}${fmt(data.total)}*\n\n`
  } else {
    msg += `💰 Subtotal: ${cs}${fmt(data.subtotal)}\n`
    if (data.deliveryFee > 0) {
      msg += `🚚 Envío: ${cs}${fmt(data.deliveryFee)}\n`
    }
    msg += `✅ *TOTAL: ${cs}${fmt(data.total)}*\n\n`
  }

  msg += `🚚 Entrega: ${formatDeliveryType(data.deliveryType)}\n`
  msg += `💳 Pago: ${formatPaymentMethod(data.paymentMethod)}\n`

  // Address block:
  //   - delivery: show the customer's delivery address + their GPS.
  //   - pickup / dine_in: never show the customer's saved address; show
  //     the restaurant pickup address when available so the customer (and
  //     the kitchen) have a clear reference point.
  if (data.deliveryType === 'delivery') {
    if (data.deliveryAddress) {
      msg += `\n📍 *Dirección:*\n${data.deliveryAddress}\n`
    }
    if (data.mapsUrl) {
      msg += `🗺️ ${data.mapsUrl}\n`
    }
  } else if (data.restaurantAddress) {
    const label = data.deliveryType === 'pickup' ? 'Retiro en' : 'Local'
    msg += `\n📍 *${label}:*\n${data.restaurantAddress}\n`
    if (data.restaurantMapsUrl) {
      msg += `🗺️ ${data.restaurantMapsUrl}\n`
    }
  }

  msg += `\n📝 Notas: ${data.notes || 'Sin notas'}\n`

  // When the customer paid by SINPE for a delivery and we have a Cloudinary
  // proof URL, swap the tracking section for the payment proof block.
  const useSinpeProofBlock =
    data.deliveryType === 'delivery' &&
    data.paymentMethod === 'sinpe' &&
    !!data.paymentProofUrl

  if (useSinpeProofBlock) {
    msg += `\n💸 *COMPROBANTE DE PAGO*\n`
    msg += `El cliente adjuntó comprobante de pago SINPE:\n`
    msg += `${data.paymentProofUrl}\n`
  } else if (data.customerTrackingVisible !== false) {
    msg += `\n🔎 *Seguimiento:*\n${trackingUrl}\n`
  }

  if (data.ticketImageUrl) {
    // Use a short on-domain ticket viewer URL instead of the raw Cloudinary URL.
    // Some Android WhatsApp clients mis-parse very long Cloudinary links, causing
    // the in-app browser to open the bare host (and 404). The viewer at
    // /ticket/:orderNumber is a thin wrapper that fetches the order and shows
    // the image — same image, but a reliable click target.
    const ticketViewerUrl = data.orderNumber
      ? `${window.location.origin}/ticket/${encodeURIComponent(data.orderNumber)}`
      : data.ticketImageUrl
    msg += `\n🧾 *Ticket:*\n${ticketViewerUrl}\n`
  }

  return msg
}

export function buildWhatsAppUrl(
  whatsappNumber: string,
  message: string,
): string {
  const cleanNumber = whatsappNumber.replace(/\D/g, '')
  return `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`
}
